package com.checkin.host.ui.layout

import androidx.compose.runtime.*
import com.checkin.host.api.ApiClient
import com.checkin.host.ui.components.BrandLogo
import com.checkin.host.theme.AppColors
import com.checkin.host.theme.AppThemeHolder
import com.checkin.host.theme.ThemeMode
import com.checkin.host.util.gestionSsoUrl
import com.checkin.host.util.gestionUrl
import com.checkin.shared.dto.UserDto
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

enum class NavPage(val label: String, val icon: String, val showInMenu: Boolean = true) {
    DASHBOARD("Tableau de bord", "⊞"),
    PROPERTIES("Hébergements", "⌂"),
    RESERVATIONS("Réservations", "◷"),
    CONTRACTS("Contrats", "⊟"),
    SUBSCRIPTION("Abonnement", "✦"),
    PROFILE("Profil", "◯", showInMenu = false),
}

@Composable
fun AppShell(
    user: UserDto,
    current: NavPage,
    onNavigate: (NavPage) -> Unit,
    onLogout: () -> Unit,
    content: @Composable () -> Unit,
) {
    AppThemeHolder.modeState.value
    val isMobile = rememberIsMobile()
    var menuOpen by remember { mutableStateOf(false) }

    fun closeMenu() { menuOpen = false }
    fun navigate(page: NavPage) { onNavigate(page); closeMenu() }

    LaunchedEffect(isMobile) { if (!isMobile) menuOpen = false }

    DisposableEffect(menuOpen) {
        document.body?.asDynamic()?.style?.overflow = if (menuOpen) "hidden" else ""
        onDispose { document.body?.asDynamic()?.style?.overflow = "" }
    }

    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(if (isMobile) FlexDirection.Column else FlexDirection.Row)
            if (isMobile) {
                property("min-height", "auto")
            } else {
                property("height", "100dvh")
                property("max-height", "100dvh")
                overflow("hidden")
            }
            width(100.percent)
            property("max-width", "100vw")
            overflowX("hidden")
            fontFamily("'Inter', system-ui", "-apple-system", "sans-serif")
            backgroundColor(AppColors.bg)
            color(AppColors.text)
        }
    }) {
        if (isMobile) {
            MobileTopBar(menuOpen = menuOpen, onToggleMenu = { menuOpen = !menuOpen })
            MainContent(isMobile = true, content = content)
            if (menuOpen) {
                Div({
                    style {
                        position(Position.Fixed)
                        property("inset", "0")
                        backgroundColor(Color(AppColors.overlayScrim))
                        property("z-index", "150")
                        property("backdrop-filter", "blur(2px)")
                    }
                    onClick { closeMenu() }
                })
                MobileSidebarDrawer(user = user, current = current, onNavigate = ::navigate, onLogout = { closeMenu(); onLogout() })
            }
        } else {
            DesktopSidebar(user = user, current = current, onNavigate = ::navigate, onLogout = onLogout)
            MainContent(isMobile = false, content = content)
        }
    }
}

@Composable
private fun MainContent(isMobile: Boolean, content: @Composable () -> Unit) {
    Div({
        style {
            flex(1)
            minWidth(0.px)
            if (!isMobile) {
                minHeight(0.px)
                overflowY("auto")
                overflowX("hidden")
            }
            width(100.percent)
            padding(if (isMobile) 16.px else 28.px, if (isMobile) 14.px else 32.px)
            backgroundColor(AppColors.bg)
            position(Position.Relative)
        }
    }) {
        if (!isMobile) {
            Div({
                style {
                    position(Position.Sticky)
                    property("top", "0")
                    property("z-index", "20")
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.FlexEnd)
                    marginBottom(8.px)
                }
            }) {
                ThemeToggleIconButton()
            }
        }
        content()
    }
}

@Composable
private fun MobileSidebarDrawer(
    user: UserDto,
    current: NavPage,
    onNavigate: (NavPage) -> Unit,
    onLogout: () -> Unit,
) {
    Div({
        style {
            position(Position.Fixed)
            property("left", "0")
            property("top", "0")
            property("bottom", "0")
            width(260.px)
            property("z-index", "200")
            property("box-shadow", "4px 0 32px rgba(0,0,0,0.5)")
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            backgroundColor(AppColors.sidebar)
            property("border-right", "1px solid ${AppColors.borderLight}")
            padding(24.px, 16.px)
        }
    }) {
        SidebarContent(user = user, current = current, showLogo = true, onNavigate = onNavigate, onLogout = onLogout)
    }
}

@Composable
private fun DesktopSidebar(
    user: UserDto,
    current: NavPage,
    onNavigate: (NavPage) -> Unit,
    onLogout: () -> Unit,
) {
    Div({
        style {
            width(240.px)
            minWidth(240.px)
            property("flex-shrink", "0")
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            backgroundColor(AppColors.sidebar)
            property("border-right", "1px solid ${AppColors.borderLight}")
            padding(24.px, 16.px)
            property("height", "100%")
            overflowY("auto")
            overflowX("hidden")
        }
    }) {
        SidebarContent(user = user, current = current, showLogo = true, onNavigate = onNavigate, onLogout = onLogout)
    }
}

@Composable
private fun SidebarContent(
    user: UserDto,
    current: NavPage,
    showLogo: Boolean,
    onNavigate: (NavPage) -> Unit,
    onLogout: () -> Unit,
) {
    if (showLogo) {
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(10.px)
                padding(0.px, 6.px)
                marginBottom(32.px)
            }
        }) {
            BrandLogo()
        }
    }

    // Section label
    Div({
        style {
            fontSize(10.px)
            fontWeight("600")
            property("text-transform", "uppercase")
            letterSpacing(1.px)
            color(AppColors.muted)
            padding(0.px, 10.px)
            marginBottom(6.px)
        }
    }) { Text("Navigation") }

    NavPage.entries.filter { it.showInMenu }.forEach { page ->
        NavItem(page = page, active = page == current, onClick = { onNavigate(page) })
    }

    gestionUrl()?.let {
        GestionNavButton()
    }

    // Spacer
    Div({ style { flex(1); minHeight(0.px) } }) {}

    // Footer
    Div({
        style {
            paddingTop(16.px)
            property("border-top", "1px solid ${AppColors.borderLight}")
        }
    }) {
        ProfileNavButton(
            user = user,
            active = current == NavPage.PROFILE,
            onClick = { onNavigate(NavPage.PROFILE) },
        )
        Button({
            style {
                width(100.percent)
                padding(8.px, 12.px)
                marginTop(8.px)
                backgroundColor(Color("transparent"))
                color(AppColors.muted)
                border(1.px, LineStyle.Solid, AppColors.border)
                borderRadius(7.px)
                cursor("pointer")
                fontSize(13.px)
                fontWeight("500")
                property("text-align", "left")
                property("transition", "background 0.12s, color 0.12s")
            }
            onClick { onLogout() }
        }) { Text("Déconnexion") }
    }
}

@Composable
private fun MobileTopBar(menuOpen: Boolean, onToggleMenu: () -> Unit) {
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(12.px)
            padding(0.px, 14.px)
            height(52.px)
            backgroundColor(AppColors.sidebar)
            property("border-bottom", "1px solid ${AppColors.borderLight}")
            position(Position.Sticky)
            property("top", "0")
            property("z-index", "90")
            property("flex-shrink", "0")
        }
    }) {
        Button({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center)
                width(40.px)
                height(40.px)
                padding(0.px)
                border(1.px, LineStyle.Solid, AppColors.border)
                borderRadius(8.px)
                backgroundColor(AppColors.surfaceHigh)
                color(AppColors.textSub)
                fontSize(18.px)
                cursor("pointer")
                lineHeight("1")
            }
            onClick { onToggleMenu() }
        }) { Text(if (menuOpen) "✕" else "☰") }
        Div({ style { flex(1); minWidth(0.px) } }) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.SpaceBetween)
                    gap(8.px)
                    height(36.px)
                }
            }) {
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        height(100.percent)
                        lineHeight("1")
                    }
                }) {
                    BrandLogo(size = 28.px, wordmarkSize = 15.px)
                }
                ThemeToggleIconButton(compact = true)
            }
        }
    }
}

@Composable
private fun ProfileNavButton(user: UserDto, active: Boolean, onClick: () -> Unit) {
    val initials = user.displayName.split(" ").take(2).joinToString("") { it.firstOrNull()?.uppercase() ?: "" }
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(10.px)
            padding(8.px, 10.px)
            borderRadius(8.px)
            marginBottom(8.px)
            width(100.percent)
            cursor("pointer")
            backgroundColor(if (active) Color(AppColors.navActiveBg) else Color.transparent)
            border(1.px, LineStyle.Solid, if (active) Color(AppColors.navActiveBorder) else Color.transparent)
            property("transition", "background 0.12s ease, border-color 0.12s ease")
        }
        onClick { onClick() }
    }) {
        Div({
            style {
                width(32.px)
                height(32.px)
                borderRadius(50.percent)
                property("background", "linear-gradient(135deg, #6366F1, #818CF8)")
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center)
                fontSize(12.px)
                fontWeight("700")
                color(Color.white)
                property("flex-shrink", "0")
            }
        }) { Text(initials.ifEmpty { "?" }) }
        Div({
            style {
                flex(1)
                minWidth(0.px)
                fontSize(13.px)
                fontWeight(if (active) "600" else "500")
                color(if (active) AppColors.navActive else AppColors.textSub)
                property("overflow", "hidden")
                property("text-overflow", "ellipsis")
                property("white-space", "nowrap")
            }
        }) { Text(user.displayName) }
        Span({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center)
                width(20.px)
                fontSize(20.px)
                fontWeight("600")
                color(if (active) AppColors.navActive else AppColors.muted)
                property("flex-shrink", "0")
                lineHeight("1")
            }
        }) { Text("›") }
    }
}

@Composable
private fun GestionNavButton() {
    Div({
        style {
            marginTop(12.px)
            paddingTop(12.px)
            property("border-top", "1px solid ${AppColors.borderLight}")
        }
    }) {
        Div({
            style {
                fontSize(10.px)
                fontWeight("600")
                property("text-transform", "uppercase")
                letterSpacing(1.px)
                color(AppColors.muted)
                padding(0.px, 10.px)
                marginBottom(6.px)
            }
        }) { Text("Modules") }

        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(10.px)
                padding(10.px, 12.px)
                borderRadius(8.px)
                marginBottom(2.px)
                cursor("pointer")
                fontSize(14.px)
                fontWeight("500")
                color(AppColors.textSub)
                backgroundColor(Color("transparent"))
                border(1.px, LineStyle.Solid, AppColors.border)
                property("transition", "background 0.12s ease, border-color 0.12s ease")
            }
            onClick {
                val token = ApiClient.token
                val url = if (!token.isNullOrBlank()) gestionSsoUrl(token) else gestionUrl()
                if (url != null) window.open(url, "_blank")
            }
        }) {
            Span({
                style {
                    width(18.px)
                    property("text-align", "center")
                    fontSize(15.px)
                    opacity(0.85)
                }
            }) { Text("◫") }
            Text("Gestion locative")
            Span({
                style {
                    marginLeft(4.px)
                    fontSize(11.px)
                    color(AppColors.muted)
                }
            }) { Text("↗") }
        }
    }
}

@Composable
private fun NavItem(page: NavPage, active: Boolean, onClick: () -> Unit) {
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(10.px)
            padding(10.px, 12.px)
            borderRadius(8.px)
            marginBottom(2.px)
            cursor("pointer")
            fontSize(14.px)
            fontWeight(if (active) "600" else "400")
            color(if (active) AppColors.navActive else AppColors.muted)
            backgroundColor(if (active) Color(AppColors.navActiveBg) else Color.transparent)
            border(1.px, LineStyle.Solid, if (active) Color(AppColors.navActiveBorder) else Color.transparent)
            property("transition", "background 0.12s ease, color 0.12s ease")
        }
        onClick { onClick() }
    }) {
        Span({
            style {
                width(18.px)
                property("text-align", "center")
                fontSize(15.px)
                opacity(if (active) 1.0 else 0.7)
            }
        }) { Text(page.icon) }
        Text(page.label)
    }
}

@Composable
fun PageHeader(title: String, subtitle: String? = null) {
    val isMobile = rememberIsMobile()
    Div({ style { marginBottom(if (isMobile) 16.px else 24.px) } }) {
        H1({
            style {
                fontSize(if (isMobile) 20.px else 24.px)
                fontWeight("700")
                color(AppColors.text)
                margin(0.px)
                letterSpacing((-0.4).px)
            }
        }) { Text(title) }
        subtitle?.let {
            P({
                style {
                    color(AppColors.muted)
                    margin(5.px, 0.px, 0.px, 0.px)
                    fontSize(14.px)
                    lineHeight("1.5")
                }
            }) { Text(it) }
        }
    }
}

@Composable
fun ThemeToggleIconButton(compact: Boolean = false) {
    AppThemeHolder.modeState.value
    val isObsidian = AppThemeHolder.mode == ThemeMode.OBSIDIAN
    Button({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            width(36.px)
            height(36.px)
            padding(0.px)
            border(1.px, LineStyle.Solid, AppColors.border)
            borderRadius(50.percent)
            backgroundColor(AppColors.surface)
            color(AppColors.textSub)
            cursor("pointer")
            fontSize(17.px)
            lineHeight("1")
            if (compact) property("margin-top", "1px")
            if (!compact) property("box-shadow", AppColors.cardShadow)
            property("transition", "background 0.15s ease, border-color 0.15s ease")
        }
        attr("title", if (isObsidian) "Passer au thème clair" else "Passer au thème Obsidian")
        attr("aria-label", if (isObsidian) "Thème clair" else "Thème Obsidian")
        onClick { AppThemeHolder.toggle() }
    }) {
        Span({
            style {
                display(DisplayStyle.Block)
                lineHeight("1")
                if (compact) property("padding-top", "1px")
            }
        }) {
            Text(if (isObsidian) "☀" else "☾")
        }
    }
}

@Composable
fun AlertBanner(message: String, isError: Boolean) {
    Div({
        style {
            padding(12.px, 16.px)
            borderRadius(10.px)
            marginBottom(16.px)
            if (isError) {
                backgroundColor(Color(AppColors.dangerBg))
                color(AppColors.danger)
                border(1.px, LineStyle.Solid, Color(AppColors.dangerBorder))
            } else {
                backgroundColor(Color(AppColors.successBg))
                color(AppColors.success)
                border(1.px, LineStyle.Solid, Color(AppColors.successBorder))
            }
            fontSize(14.px)
        }
    }) { Text(message) }
}
